/**
 * 📋 SDLC 6.3.1 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/types/errors.ts
 * PURPOSE: Component implementation aligned with SDLC 6.3.1 standards
 * SDLC FRAMEWORK VERSION: 6.1.1 (7-Pillar + 3-Section + Long-Running Agent Protocol)
 * PILLAR: P1 (Stage 06 - BUILD) + P2 (Sprint Governance)
 * SPRINT GOVERNANCE: Rule 2: Milestone-Gated Roadmaps
 *
 * CTO MANDATE: Production-ready React component with enterprise quality standards
 *
 * @business-context
 * - Domain: Type Definitions, API Contracts, Vietnamese F&B Data Models
 * - Impact: Type Safety, API Consistency, Developer Experience
 * - Stakeholders: Development Team, Backend Team, Frontend Team
 *
 * @technical-architecture
 * - Layer: Type System, Data Contracts
 * - Pattern: TypeScript Interfaces, Type Guards, Schema Validation
 * - Dependencies: TypeScript, Zod (optional)
 */

export enum ErrorCode {
  // Authentication errors
  INVALID_CREDENTIALS = "INVALID_CREDENTIALS",
  INVALID_OTP = "INVALID_OTP",
  SESSION_EXPIRED = "SESSION_EXPIRED",
  UNAUTHORIZED = "UNAUTHORIZED",

  // Location errors
  LOCATION_PERMISSION_DENIED = "LOCATION_PERMISSION_DENIED",
  LOCATION_UNAVAILABLE = "LOCATION_UNAVAILABLE",
  LOCATION_TIMEOUT = "LOCATION_TIMEOUT",
  LOCATION_OUT_OF_RANGE = "LOCATION_OUT_OF_RANGE",

  // Attendance errors
  NO_SCHEDULE = "NO_SCHEDULE",
  ALREADY_CLOCKED_IN = "ALREADY_CLOCKED_IN",
  NOT_CLOCKED_IN = "NOT_CLOCKED_IN",
  OUTSIDE_SCHEDULE = "OUTSIDE_SCHEDULE",

  // Network errors
  NETWORK_ERROR = "NETWORK_ERROR",
  SERVER_ERROR = "SERVER_ERROR",
  TIMEOUT = "TIMEOUT",

  // Validation errors
  VALIDATION_ERROR = "VALIDATION_ERROR",
  INVALID_PHONE = "INVALID_PHONE",

  // Unknown
  UNKNOWN = "UNKNOWN",
}

export interface ErrorDetail {
  code: ErrorCode;
  message: string;
  field?: string;
  context?: Record<string, any>;
}

export class AppError extends Error {
  constructor(
    public code: ErrorCode,
    message: string,
    public statusCode?: number,
    public context?: Record<string, any>,
  ) {
    super(message);
    this.name = "AppError";
  }
}

// Error message mapping - maps to translation keys
export const ERROR_MESSAGE_KEYS: Record<ErrorCode, string> = {
  // Authentication
  [ErrorCode.INVALID_CREDENTIALS]: 'errors.invalid_credentials',
  [ErrorCode.INVALID_OTP]: 'errors.invalid_otp',
  [ErrorCode.SESSION_EXPIRED]: 'errors.session_expired',
  [ErrorCode.UNAUTHORIZED]: 'errors.unauthorized',

  // Location
  [ErrorCode.LOCATION_PERMISSION_DENIED]: 'errors.location_permission_denied',
  [ErrorCode.LOCATION_UNAVAILABLE]: 'errors.location_unavailable',
  [ErrorCode.LOCATION_TIMEOUT]: 'errors.location_timeout',
  [ErrorCode.LOCATION_OUT_OF_RANGE]: 'errors.location_out_of_range',

  // Attendance
  [ErrorCode.NO_SCHEDULE]: 'errors.no_schedule',
  [ErrorCode.ALREADY_CLOCKED_IN]: 'errors.already_clocked_in',
  [ErrorCode.NOT_CLOCKED_IN]: 'errors.not_clocked_in',
  [ErrorCode.OUTSIDE_SCHEDULE]: 'errors.outside_schedule',

  // Network
  [ErrorCode.NETWORK_ERROR]: 'errors.network',
  [ErrorCode.SERVER_ERROR]: 'errors.server',
  [ErrorCode.TIMEOUT]: 'errors.timeout',

  // Validation
  [ErrorCode.VALIDATION_ERROR]: 'errors.validation',
  [ErrorCode.INVALID_PHONE]: 'errors.invalid_phone',

  // Unknown
  [ErrorCode.UNKNOWN]: 'errors.unknown',
};

/**
 * Get error translation key
 */
export function error_key_extract__parses_error__returns_key(
  error: unknown,
): { key: string; params?: Record<string, any> } {
  if (error instanceof AppError) {
    // Special handling for location out of range
    if (error.code === ErrorCode.LOCATION_OUT_OF_RANGE && error.context) {
      const { distance, allowedRadius } = error.context;
      return {
        key: 'errors.location_out_of_range_detailed',
        params: { distance: Math.round(distance), allowedRadius }
      };
    }
    return { key: ERROR_MESSAGE_KEYS[error.code] || 'errors.unknown' };
  }

  if (error instanceof Error) {
    // Check for specific error patterns
    if (error.message.includes("Network")) {
      return { key: ERROR_MESSAGE_KEYS[ErrorCode.NETWORK_ERROR] };
    }

    // Return the raw message for unknown errors
    return { key: 'errors.unknown', params: { message: error.message } };
  }

  return { key: ERROR_MESSAGE_KEYS[ErrorCode.UNKNOWN] };
}

/**
 * Legacy function - returns translation key instead of hardcoded message
 * @deprecated Use error_key_extract__parses_error__returns_key instead
 */
export function error_message_extract__parses_error__returns_string(
  error: unknown,
): string {
  const { key } = error_key_extract__parses_error__returns_key(error);
  return key; // Return translation key for backwards compatibility
}

/**
 * Parse API error response
 */
export function parseApiError(status: number, data: any): ErrorCode {
  // Map HTTP status codes
  if (status === 401) return ErrorCode.UNAUTHORIZED;
  if (status === 403) return ErrorCode.UNAUTHORIZED;
  if (status >= 500) return ErrorCode.SERVER_ERROR;

  // Map specific error details
  const detailRaw = data?.detail;
  const detail = typeof detailRaw === 'string' ? detailRaw.toLowerCase() : '';

  if (detail.includes("otp")) return ErrorCode.INVALID_OTP;
  if (detail.includes("phone")) return ErrorCode.INVALID_PHONE;
  if (detail.includes("location")) return ErrorCode.LOCATION_OUT_OF_RANGE;
  if (detail.includes("schedule")) return ErrorCode.NO_SCHEDULE;
  if (detail.includes("already clocked")) return ErrorCode.ALREADY_CLOCKED_IN;
  if (detail.includes("not clocked")) return ErrorCode.NOT_CLOCKED_IN;

  return ErrorCode.UNKNOWN;
}
